import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_URL, buildKioskAuthHeaders } from './api';
import NumericKeypad from './NumericKeypad';
import { clearCustomerSession, getCustomerSessionAccountId, saveCustomerSession } from './kioskCustomerSession';

function LoyaltyEntryScreen() {
  const navigate = useNavigate();
  const inactivityTimerRef = useRef(null);
  const activeRef = useRef(true);
  useEffect(() => { activeRef.current = true; return () => { activeRef.current = false; }; }, []);
  const [mode, setMode] = useState('');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const formattedPhone = useMemo(() => {
    return phone.replace(/(\d{2})(?=\d)/g, '$1 ').trim();
  }, [phone]);

  useEffect(() => {
    const goBackToAttract = () => {
      navigate('/veille');
    };

    const resetInactivityTimer = () => {
      if (inactivityTimerRef.current) {
        window.clearTimeout(inactivityTimerRef.current);
      }
      inactivityTimerRef.current = window.setTimeout(goBackToAttract, 20000);
    };

    const events = ['pointerdown', 'pointermove', 'keydown', 'touchstart', 'click', 'scroll'];
    events.forEach((eventName) => window.addEventListener(eventName, resetInactivityTimer, { passive: true }));
    resetInactivityTimer();

    return () => {
      events.forEach((eventName) => window.removeEventListener(eventName, resetInactivityTimer));
      if (inactivityTimerRef.current) {
        window.clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [navigate]);

  const handleContinueAsGuest = () => {
    clearCustomerSession();
    navigate('/client');
  };

  const handleSubmit = async () => {
    if (phone.length < 10) {
      setError('Entre un numéro de téléphone valide.');
      return;
    }

    const accountId = getCustomerSessionAccountId();
    if (!accountId) { setError('Identifiez cette borne avant de connecter un client.'); return; }
    setLoading(true);
    setError('');

    try {
      const endpoint = mode === 'register' ? 'register' : 'login';
      const response = await fetch(`${API_URL}/api/customers/${endpoint}`, {
        method: 'POST',
        headers: buildKioskAuthHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ phone }),
      });

      const payload = await response.json();
      if (!response.ok) {
        throw new Error(payload.error || 'Impossible de continuer.');
      }

      if (!activeRef.current || accountId !== getCustomerSessionAccountId()) return;
      if (!saveCustomerSession(payload, accountId)) throw new Error('Enregistrement du compte client impossible sur cet appareil.');
      navigate('/client');
    } catch (submitError) {
      if (activeRef.current && accountId === getCustomerSessionAccountId()) setError(submitError.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="loyalty-entry-page">
      <div className="loyalty-entry-card">
        <span className="kiosk-eyebrow">Fidelisation</span>
        <h1>Mon compte</h1>
        <p>Connecte-toi avec ton numero de telephone ou continue directement vers la borne.</p>

        {!mode ? (
          <div className="loyalty-entry-actions">
            <button type="button" className="kiosk-cta" onClick={() => setMode('login')}>
              Mon compte
            </button>
            <button type="button" className="kiosk-cta secondary" onClick={() => setMode('register')}>
              Creer un compte
            </button>
            <button type="button" className="kiosk-cta secondary" onClick={handleContinueAsGuest}>
              Continuer sans se connecter
            </button>
          </div>
        ) : (
          <>
            <div className="loyalty-entry-head">
              <div>
                <h2>{mode === 'register' ? 'Creer un compte' : 'Connexion'}</h2>
                <p>Ton numero de telephone devient ton identifiant client.</p>
              </div>
              <button type="button" className="kiosk-modal-close" onClick={() => { setMode(''); setPhone(''); setError(''); }}>
                ×
              </button>
            </div>

            <div className="loyalty-phone-display">
              <span>{formattedPhone}</span>
            </div>

            <NumericKeypad
              value={phone}
              maxLength={10}
              onChange={(nextPhone) => {
                setError('');
                setPhone(nextPhone);
              }}
            />

            {error && <div className="loyalty-entry-error">{error}</div>}

            <div className="loyalty-entry-actions loyalty-entry-actions-inline">
              <button type="button" className="kiosk-cta secondary" onClick={handleContinueAsGuest}>
                Continuer sans se connecter
              </button>
              <button type="button" className="kiosk-cta" onClick={handleSubmit} disabled={loading}>
                {loading ? 'Chargement...' : mode === 'register' ? 'Creer le compte' : 'Se connecter'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default LoyaltyEntryScreen;
